/**
 * ConfigPanel
 * -----------
 * 
 * September 2026
 */

import ActionPanel from './actions/ActionPanel';
import ConfigValidation from './ConfigValidation';
import ReloadAction from './ReloadAction';
import EntityTypePanel from './EntityTypePanel';
import ExperimentPanel from './ExperimentPanel';
import ResourcePanel from './resources/ResourcePanel';
import SystemPanel from './SystemPanel';

import * as React from 'react';

import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { FileUpload } from "primereact/fileupload";
import { ListBox } from 'primereact/listbox';
import { ProgressBar } from 'primereact/progressbar';
import { TabView, TabPanel } from 'primereact/tabview';
import { Toast } from "primereact/toast";

import parser from "papaparse";

/**
 * Configuration panel for setting up the simulation model's parameters and attributes.
 *
 * @param {Object} configState          - State for managing the simulation model's configuration.
 * @param {object} elementState         - State for managing the currently selected entity type, action, & resource elements.
 * @param {Function} simulateDiagram    - Method for running the DES simulation with the current configuration.
 * @param {boolean} isLoading           - Whether an experiment/simulation run is currently in progress.
 * @param {Object} progress             - {completed, total} trial counts for the currently-running job, polled from the backend.
 * @param {Function} cancelRun          - Requests cancellation of the running job.
 *
 * @todo Apply prettier on code.
 */
function ConfigPanel({ configState, elementState, simulateDiagram, isLoading, progress, cancelRun, onExperimentSetupChange }) {
    const { config, setConfig, configWarnings, setConfigWarnings, reachedConfig, setReachedConfig } = configState;
    const { selectedEntityType, setEntityType, selectedAction, setAction, selectedResource, setResource } = elementState;

    // State hook determining how entity types will be configured.
    const [entityConfigEnabled, setEntityConfig] = React.useState(false);

    // State hook managing the current parameter ids used for experimentation.
    const [selectedParamIds, setSelectedParamIds] = React.useState([]);

    // State hook managing the updates made to selectable parameters.
    const [paramUpdates, setParamUpdates] = React.useState({});

    const [numLevels, setNumLevels] = React.useState(2);

    const [isDistributionOverlayOpen, setIsDistributionOverlayOpen] = React.useState(false);

    // Ref hook for the FileUpload component.
    const fileUploadRef = React.useRef(null);

    // Ref hook for the toast component displaying .csv parsing info.
    const csvParsingToast = React.useRef(null);

    // Determines the list of parameters available for experimentation.
    const availableParams = React.useMemo(() => {
        let newAvailability = [];

        // Lists entity count & inter-arrival time.
        Object.values(config.entity_types).forEach(entityType => {
            newAvailability.push({'id': `entity-${entityType.gid}-entities-per-arrival`, 'displayName': `${entityType.name} entities per arrival`, 'name': entityType.name, 'parameter': 'entities_per_arrival', 'param_arg': null, 'constant': null,
                'dist': 'uniform', 'min': null, 'max': null, 'arg3': null, 'arg4': null, 'type': 'entity_type', 'gid': entityType.gid});
            newAvailability.push({'id': `entity-${entityType.gid}-interarrival`, 'displayName': `${entityType.name} interarrival`, 'name': entityType.name, 'parameter': 'interarrival', 'param_arg': null, 'constant': null,
                'dist': 'uniform', 'min': null, 'max': null, 'arg3': null, 'arg4': null, 'type': 'entity_type', 'gid': entityType.gid});
        });

        // Lists action times & resource request amounts.
        Object.values(config.actions).forEach(action => {
            newAvailability.push({'id': `action-${action.gid}-time`, 'displayName': `${action.name} time`, 'name': action.name, 'parameter': 'time', 'param_arg': null, 'constant': null,
                'dist': 'uniform', 'min': null, 'max': null, 'arg3': null, 'arg4': null, 'type': 'action', 'gid': action.gid});

            Object.entries(action.resources).forEach(([resource_gid, resource]) => {
                const resource_name = config.resources[resource_gid].name;
                const request_amount = resource.request_amount;

                newAvailability.push({'id': `action-${action.gid}-request-${resource_gid}`, 'displayName': `${action.name}-${resource_name} request amount`, 'name': `${action.name}-${resource_name}`,
                    'parameter': `request_amount`, 'param_arg': `${resource_gid}`, 'constant': null, 'dist': 'uniform', 'min': null, 'max': null, 'arg3': null, 'arg4': null, 'type': 'action', 'gid': action.gid});
            });
        });

        // Lists resource initial amounts.
        Object.values(config.resources).forEach(resource => {
            newAvailability.push({'id': `resource-${resource.gid}-amount`, 'displayName': `${resource.name} amount`, 'name': resource.name, 'parameter': 'amount', 'param_arg': null, 'constant': resource.initialAmount,
                'dist': 'uniform', 'min': null, 'max': null, 'arg3': null, 'arg4': null, 'type': 'resource', 'gid': resource.gid});
        });

        return newAvailability;
    }, [config]);

    // Filters invalid available parameters.
    const validSelectedParamIds = React.useMemo(() => {
        const validIds = new Set(availableParams.map(param => param.id));
        return selectedParamIds.filter(id => validIds.has(id));
    }, [availableParams, selectedParamIds]);

    // Determines the list of parameters being used for experimentation.
    const selectedParameters = React.useMemo(() => {
        return availableParams
            .filter(param => selectedParamIds.includes(param.id))
            .map(param => ({...param, ...(paramUpdates[param.id] || {})}));
    }, [availableParams, validSelectedParamIds, paramUpdates]);

    // Shares the experiment panel's selections with the AI Assistant's page context.
    React.useEffect(() => {
        if (onExperimentSetupChange) onExperimentSetupChange({ selectedParameters, numLevels });
    }, [selectedParameters, numLevels]);

    // Effect hook determining which configuration to show for entity types after first entering the configuration panel.
    React.useEffect(() => {
        if ("script" in config.entity_types) {
            setEntityConfig(true);
        } else {
            setEntityConfig(false);
        }
    }, [reachedConfig]);

    /**
     * Adds a new entity type to the configuration.
     */
    const handleAddEntity = () => {
        const entityTypeGid = crypto.randomUUID();
        const newEntityType = {'gid': entityTypeGid, 'name': "Entity", 'entities_per_arrival': {'dist': 'constant', 'attr': {'value': 1}}, 'interarrival': {'dist': 'constant', 'attr': {'value': 1}}, 'capacity': {'is_enabled': false, 'value': null}};
        const newEntityTypes = {...config.entity_types, [entityTypeGid]: newEntityType};
        const newConfig = {...config, entity_types: newEntityTypes};
        setConfig(newConfig);
    }

    /**
     * Removes an entity type from the configuration.
     * 
     * @param {String} gid  - The entity type's identifier.
     */
    const handleRemoveEntity = (entityTypeGid) => {
        const newWarnings = {...configWarnings};
        delete newWarnings.entity_types[entityTypeGid];
        setConfigWarnings(newWarnings);
        
        // Remove entity type from resources' ranked lists.
        let newResources = {...config.resources};
        Object.entries(config.resources).forEach(([resourceGid, resource]) => {
            if (resource.queueDiscipline === "Priority") {
                let oldRanks = {...resource.priorityRanks};
                const {[entityTypeGid]: _, ...remainingRanks} = oldRanks;
                newResources[resourceGid].priorityRanks = remainingRanks;
            }
        })

        // Remove entity type from "OR" elements' decision branches.
        const newActions = Object.fromEntries(
            Object.entries(config.actions).map(([actionGid, action]) => {
                if (action.type === 'or' && action.or_script?.type === 'entity_type') {
                    const newDecision = (action.or_script.decision).map(branch => (
                        branch.filter(entityType => entityType !== entityTypeGid)
                    ));
                    const newOrScript = {...action.or_script, decision: newDecision};
                    const newAction = {...action, or_script: newOrScript};
                    return [actionGid, newAction];
                }
                return [actionGid, action];
            })
        );

        // Remove entity type from configuration lists.
        const newConfig = {...config, actions: newActions, resources: newResources};
        delete newConfig.entity_types[entityTypeGid];
        setConfig(newConfig);
    }

    /**
     * Switches which configuration to use for entity types.
     * 
     * @param {Boolean} checkStatus - Flag for which config components to render for entity types.
     */
    const handleEntityConfig = (checkStatus) => {
        if (checkStatus) {
            const newEntityTypes = {};
            const newConfig = {...config, "entity_types": newEntityTypes};
            setConfig(newConfig);
        } else {
            const entityTypeGID = crypto.randomUUID();
            const entityDist = { dist: "constant", attr: { value: 1 } };
            const newEntityTypes = {[entityTypeGID]: {gid: entityTypeGID, name: "Entity", entities_per_arrival: entityDist, interarrival: entityDist, capacity: { is_enabled: false, value: null }}};
            const newConfig = {...config, "entity_types": newEntityTypes};
            setConfig(newConfig);
        }

        setEntityConfig(checkStatus);
    }

    /**
     * Parses entity arrival data from a csv file into a script.
     * 
     * @param {Object} event    - File being uploaded
     * 
     * @todo Add comments
     */
    const parseEntityArrivals = async (event) => {
        const file = event.files[0];
        if (!file) {
            csvParsingToast.current.show({ severity: "error", summary: "Error", detail: "File surpassed size limit.", life: 3000});
            return;
        }

        try {
            parser.parse(file, {
                header: true,
                complete: (results) => {
                    let entityArrivalScript = [];
                    results.data.forEach(row => {
                        const isTerminatedType = (row["type"] === "MsgWeaponTerminated");
                        const hasInterceptPk = ((row["intercept pk"] ?? row["13"]) !== "0");
                        
                        if (isTerminatedType && hasInterceptPk) {
                            entityArrivalScript.push({"arrival_time": parseFloat(row.time)});
                        }
                    });

                    if (entityArrivalScript.length === 0) {
                        csvParsingToast.current.show({ severity: "error", summary: "Error", detail: "File contained no entity arrivals.", life: 3000});
                    } else {
                        const newEntityTypes = {"script": entityArrivalScript};
                        const newConfig = {...config, "entity_types": newEntityTypes};
                        setConfig(newConfig);
                        
                        console.log(newConfig);
                    }
                    fileUploadRef.current?.clear();
                }
            });
        } catch (error) {
            console.error("Failed to parse csv file: ", error.message);
            csvParsingToast.current.show({ severity: "error", summary: "Error", detail: "Failed to parse csv file.", life: 3000});
        }
    }

    // Percent complete for the determinate progress bar
    const progressTotal = progress?.total || 0;
    const progressCompleted = progress?.completed || 0;
    const progressPercent = progressTotal > 0
        ? Math.round((progressCompleted / progressTotal) * 100)
        : 0;

    return (
        <div style={{ marginTop: '1rem' }}>
            <ConfigValidation config={config} configWarnings={configWarnings} selectedParameters={selectedParameters} simulateDiagram={simulateDiagram} isLoading={isLoading} numLevels={numLevels} isDistributionOverlayOpen={isDistributionOverlayOpen} cancelRun={cancelRun}/>
            <ReloadAction config={config} setConfig={setConfig} setSelectedParamIds={setSelectedParamIds} setParamUpdates={setParamUpdates} setReachedConfig={setReachedConfig} setNumLevels={setNumLevels}/>
            {isLoading && (
                <div style={{ maxWidth: '480px', margin: '0.5rem auto 1rem' }}>
                    {progressTotal > 0 ? (
                        <>
                            <ProgressBar value={progressPercent} />
                            <div style={{ textAlign: 'center', fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                                Running trial {progressCompleted} of {progressTotal}
                            </div>
                        </>
                    ) : (
                        <>
                            <ProgressBar mode="indeterminate" style={{ height: '6px' }} />
                            <div style={{ textAlign: 'center', fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                                Starting simulation…
                            </div>
                        </>
                    )}
                </div>
            )}
            <TabView pt={{ nav: { style: { justifyContent: 'center' }}}} renderActiveOnly={false}>
                <TabPanel header="System">
                    <div className='flex gap-4' style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
                        <SystemPanel config={config} setConfig={setConfig} configWarnings={configWarnings} setConfigWarnings={setConfigWarnings}/>
                    </div>
                </TabPanel>
                <TabPanel header="Entity Types">
                    <div style={{ marginBottom: "1rem" }}>
                        <Checkbox checked={entityConfigEnabled} onChange={(event) => handleEntityConfig(event.checked)} style={{ marginRight: '0.5rem' }}></Checkbox>
                        <label style={{ marginRight: "1rem"}}>Enable Entity Script</label>
                    </div>
                    {entityConfigEnabled &&
                        <div className='flex gap-4' style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
                            <Toast ref={csvParsingToast} position="center"/>
                            <FileUpload
                                ref={fileUploadRef}
                                mode="basic"
                                auto
                                customUpload
                                onSelect={(event) => parseEntityArrivals(event)}
                                chooseLabel="Upload Entity Script"
                                accept=".csv,text/csv"
                                maxFileSize={1000000}
                            />
                        </div>
                    }
                    {!entityConfigEnabled && 
                        <div className='flex gap-4' style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
                            <ListBox
                                value={selectedEntityType}
                                onChange={(event) => {if (event.value === null || event.value === undefined) {return;} setEntityType(event.value)}}
                                options={Object.entries(config.entity_types || {}).map(([gidKey, entityType]) => ({label: entityType.name === "" ? "..." : entityType.name, value: gidKey}))}
                                optionLabel="label"
                                optionValue="value"
                                filter
                                className='w-full md:w-14rem'
                                style={{ maxHeight: '600px', overflowY: 'auto' }}
                            />
                            <div>
                                <EntityTypePanel config={config} setConfig={setConfig} entityType={config.entity_types?.[selectedEntityType]} configWarnings={configWarnings} setConfigWarnings={setConfigWarnings}/>
                                <Button label="Add Entity Type" onClick={() => handleAddEntity()} style={{ marginRight: '1rem' }}/>
                                <Button label="Remove Entity Type" onClick={() => handleRemoveEntity(selectedEntityType)} disabled={Object.keys(config.entity_types).length === 1}/>
                            </div>
                        </div>
                    }
                </TabPanel>
                <TabPanel header="Actions">
                    <div className='flex gap-4' style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center' }}>
                        <ListBox
                            value={selectedAction}
                            onChange={(event) => {if (event.value === null || event.value === undefined) {return;} setAction(event.value)}}
                            options={Object.entries(config.actions || {}).map(([gidKey, value]) => ({label: value.name === "" ? gidKey : value.name, value: gidKey}))}
                            optionLabel="label"
                            optionValue="value"
                            filter
                            className='w-full md:w-14rem'
                            listStyle={{ height: '430px' }}
                            style={{ width: '220px' }}
                        />
                        <ActionPanel config={config} setConfig={setConfig} action={config.actions?.[selectedAction]} configWarnings={configWarnings} setConfigWarnings={setConfigWarnings} reachedConfig={reachedConfig}/>
                    </div>
                </TabPanel>
                <TabPanel header="Resources">
                    <div className='flex gap-4' style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: '1.5rem' }}>
                        <ListBox
                            value={selectedResource}
                            onChange={(event) => {if (event.value === null || event.value === undefined) {return;} setResource(event.value)}}
                            options={Object.entries(config.resources || {}).map(([gidKey, value]) => ({label: value.name === "" ? gidKey : value.name, value: gidKey}))}
                            optionLabel="label"
                            optionValue="value"
                            filter
                            className='w-full md:w-14rem'
                            listStyle={{ height: '430px' }}
                            style={{ width: '220px' }}/>
                        <div style={{ minWidth: '280px' }}>
                            <ResourcePanel config={config} setConfig={setConfig} resource={config.resources?.[selectedResource]} configWarnings={configWarnings} setConfigWarnings={setConfigWarnings} reachedConfig={reachedConfig}/>
                        </div>
                    </div>
                </TabPanel>
                <TabPanel header="Experimentation">
                    <ExperimentPanel
                        config={config} setConfig={setConfig}
                        selectedParamIds={selectedParamIds} setSelectedParamIds={setSelectedParamIds}
                        paramUpdates={paramUpdates} setParamUpdates={setParamUpdates}
                        availableParams={availableParams} validSelectedParamIds={validSelectedParamIds} selectedParameters={selectedParameters}
                        numLevels={numLevels} setNumLevels={setNumLevels}
                        setIsDistributionOverlayOpen={setIsDistributionOverlayOpen}
                    />
                </TabPanel>
            </TabView>
        </div>
    )
}

export default ConfigPanel;